import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Copy, MessageCircle, Share2, Loader2, Mail, ExternalLink, QrCode, Pencil } from "lucide-react";
import { toast } from "sonner";

const MANDATORY = ["name", "position", "company", "phone"];
function missing(profile) {
    return MANDATORY.filter((k) => !((profile?.[k] || "").trim?.() || profile?.[k]));
}

export default function ShareCard() {
    const navigate = useNavigate();
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/profile/me");
                const m = missing(data);
                if (m.length) {
                    navigate(`/profile?required=${m.join(",")}`, { replace: true });
                    return;
                }
                setProfile(data);
            } finally { setLoading(false); }
        })();
    }, [navigate]);

    if (loading) {
        return (
            <Layout title="Compartir mi tarjeta" subtitle="QR y enlace para compartir">
                <div className="py-16 flex items-center justify-center text-slate-400 text-sm">
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />Cargando...
                </div>
            </Layout>
        );
    }
    if (!profile) return null;

    const path = profile.card_slug ? `/c/${profile.card_slug}` : `/c/${profile.user_id}`;
    const url = `${window.location.origin}${path}`;

    const copy = async () => {
        try { await navigator.clipboard.writeText(url); toast.success("Enlace copiado"); }
        catch { toast.error("No se pudo copiar"); }
    };
    const share = async () => {
        const title = profile.name ? `${profile.name} · Emprenovus` : "Mi tarjeta de presentación";
        if (navigator.share) {
            try { await navigator.share({ title, url }); return; } catch { /* cancelled */ }
        }
        copy();
    };
    const whatsapp = () => {
        const text = encodeURIComponent(`${profile.name || ""}${profile.position ? " · " + profile.position : ""}\n${url}`);
        window.open(`https://wa.me/?text=${text}`, "_blank");
    };
    const email = () => {
        const subj = encodeURIComponent(`Mi tarjeta digital — ${profile.name || ""}`);
        const body = encodeURIComponent(`Hola,\n\nTe comparto mi tarjeta digital: ${url}\n\n${profile.name || ""}${profile.position ? " · " + profile.position : ""}`);
        window.open(`mailto:?subject=${subj}&body=${body}`, "_blank");
    };

    return (
        <Layout
            title="Compartir mi tarjeta"
            subtitle="Tu QR y enlace público"
            actions={
                <Button variant="outline" onClick={() => navigate("/profile")} data-testid="share-edit">
                    <Pencil className="w-4 h-4 mr-2" />Editar tarjeta
                </Button>
            }
            data-testid="share-card-page"
        >
            <div className="max-w-md mx-auto space-y-4">
                <div className="bg-white border border-slate-200 rounded-xl p-6 flex flex-col items-center text-center" data-testid="share-qr-card">
                    <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-1 justify-center">
                        <QrCode className="w-3.5 h-3.5" />Escanea o comparte
                    </div>
                    <div className="bg-white p-3 rounded-md border border-slate-200">
                        <QRCodeSVG value={url} size={208} level="M" data-testid="share-qr" />
                    </div>
                    <div className="mt-4">
                        <div className="font-display font-semibold text-slate-900">{profile.name}</div>
                        {(profile.position || profile.company) && (
                            <div className="text-xs text-slate-500 mt-0.5">
                                {profile.position}{profile.position && profile.company ? " · " : ""}{profile.company}
                            </div>
                        )}
                    </div>
                    <div className="mt-4 w-full bg-slate-50 border border-slate-200 rounded-md p-2.5 text-xs text-slate-700 break-all" data-testid="share-url">{url}</div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                    <Button onClick={copy} variant="outline" data-testid="share-copy">
                        <Copy className="w-4 h-4 mr-2" />Copiar enlace
                    </Button>
                    <Button onClick={share} data-testid="share-native">
                        <Share2 className="w-4 h-4 mr-2" />Compartir
                    </Button>
                    <Button onClick={whatsapp} variant="outline" className="border-green-500 text-green-700 hover:bg-green-50 hover:text-green-700" data-testid="share-whatsapp">
                        <MessageCircle className="w-4 h-4 mr-2" />WhatsApp
                    </Button>
                    <Button onClick={email} variant="outline" data-testid="share-email">
                        <Mail className="w-4 h-4 mr-2" />Email
                    </Button>
                </div>

                <Button variant="ghost" className="w-full" onClick={() => window.open(url, "_blank")} data-testid="share-preview">
                    <ExternalLink className="w-4 h-4 mr-2" />Ver mi tarjeta pública
                </Button>
            </div>
        </Layout>
    );
}
